import {
  CANVAS_AI_CONTENT_VERSION,
  CANVAS_AI_MAX_MESSAGES,
  CANVAS_AI_MODEL_STORAGE_KEY,
  CANVAS_AI_REMEMBERED_KEY_STORAGE_KEY,
  CANVAS_AI_SESSION_KEY_STORAGE_KEY,
} from "./canvasAi.constants";

const getLocalStorage = (storage) => storage ?? window.localStorage;
const getSessionStorage = (storage) => storage ?? window.sessionStorage;
const getConversationKey = (documentKey) =>
  `netpad.canvas-ai.chat.v${CANVAS_AI_CONTENT_VERSION}:${encodeURIComponent(
    String(documentKey)
  )}`;

const isStoredMessage = (message) =>
  Boolean(message) &&
  message.isTransient !== true &&
  ["assistant", "user"].includes(message.role) &&
  typeof message.content === "string" &&
  Boolean(message.content.trim());

const normalizeMessages = (messages) =>
  (Array.isArray(messages) ? messages : [])
    .filter(isStoredMessage)
    .map(({ content, role }) => ({ content, role }))
    .slice(-CANVAS_AI_MAX_MESSAGES);

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

/** @returns {{ apiKey: string, isRemembered: boolean }} */
export const readOpenRouterCredential = ({ localStorage, sessionStorage } = {}) => {
  const sessionKey = getSessionStorage(sessionStorage).getItem(
    CANVAS_AI_SESSION_KEY_STORAGE_KEY
  );
  if (sessionKey) return { apiKey: sessionKey, isRemembered: false };

  const rememberedKey = getLocalStorage(localStorage).getItem(
    CANVAS_AI_REMEMBERED_KEY_STORAGE_KEY
  );
  return { apiKey: rememberedKey ?? "", isRemembered: Boolean(rememberedKey) };
};

export const storeOpenRouterCredential = ({
  apiKey,
  isRemembered,
  localStorage,
  sessionStorage,
}) => {
  const cleanKey = String(apiKey).trim();
  const local = getLocalStorage(localStorage);
  const session = getSessionStorage(sessionStorage);
  local.removeItem(CANVAS_AI_REMEMBERED_KEY_STORAGE_KEY);
  session.removeItem(CANVAS_AI_SESSION_KEY_STORAGE_KEY);
  const targetStorage = isRemembered ? local : session;
  const targetKey = isRemembered
    ? CANVAS_AI_REMEMBERED_KEY_STORAGE_KEY
    : CANVAS_AI_SESSION_KEY_STORAGE_KEY;
  targetStorage.setItem(targetKey, cleanKey);
};

export const clearOpenRouterCredential = ({ localStorage, sessionStorage } = {}) => {
  getLocalStorage(localStorage).removeItem(CANVAS_AI_REMEMBERED_KEY_STORAGE_KEY);
  getSessionStorage(sessionStorage).removeItem(CANVAS_AI_SESSION_KEY_STORAGE_KEY);
};

export const readCanvasAiModel = (sessionStorage) =>
  getSessionStorage(sessionStorage).getItem(CANVAS_AI_MODEL_STORAGE_KEY) ?? "";

export const storeCanvasAiModel = (modelId, sessionStorage) => {
  const storage = getSessionStorage(sessionStorage);
  if (!modelId) return storage.removeItem(CANVAS_AI_MODEL_STORAGE_KEY);
  storage.setItem(CANVAS_AI_MODEL_STORAGE_KEY, modelId);
};

export const readCanvasAiConversation = (documentKey, sessionStorage) => {
  const rawValue = getSessionStorage(sessionStorage).getItem(
    getConversationKey(documentKey)
  );
  const parsedValue = safeParse(rawValue);
  if (parsedValue?.version !== CANVAS_AI_CONTENT_VERSION) return [];

  return normalizeMessages(parsedValue.messages);
};

export const storeCanvasAiConversation = ({
  documentKey,
  messages,
  sessionStorage,
}) => {
  getSessionStorage(sessionStorage).setItem(
    getConversationKey(documentKey),
    JSON.stringify({
      messages: normalizeMessages(messages),
      version: CANVAS_AI_CONTENT_VERSION,
    })
  );
};

export const clearCanvasAiConversation = (documentKey, sessionStorage) => {
  getSessionStorage(sessionStorage).removeItem(getConversationKey(documentKey));
};

export const migrateCanvasAiConversation = ({
  fromDocumentKey,
  sessionStorage,
  toDocumentKey,
}) => {
  if (!fromDocumentKey || !toDocumentKey || fromDocumentKey === toDocumentKey) return;
  const storage = getSessionStorage(sessionStorage);
  const sourceKey = getConversationKey(fromDocumentKey);
  const targetKey = getConversationKey(toDocumentKey);
  const sourceValue = storage.getItem(sourceKey);
  if (sourceValue && !storage.getItem(targetKey)) storage.setItem(targetKey, sourceValue);
  storage.removeItem(sourceKey);
};
